import { useState, useEffect, useCallback } from "react";
import { createConversation, sendMessage, getHistory } from "@/lib/api/chat-api";
import type { ChatMessage } from "@/lib/api/chat-api";
import { logger } from "@/utils/logger";

const CONVERSATION_ID_KEY = "chatgpt_faq_conversation_id";

export function useChat() {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  // Load conversation ID from localStorage on mount
  useEffect(() => {
    const storedId = localStorage.getItem(CONVERSATION_ID_KEY);
    if (storedId) {
      setConversationId(storedId);
      // Load history
      loadHistory(storedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getHistory(id);
      if (response.data) {
        setMessages(response.data.messages);
      }
    } catch (err) {
      logger.error("Failed to load chat history:", err);
      setError(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initializeConversation = useCallback(async () => {
    // Don't create if we already have a conversation ID or are already initializing
    if (conversationId || isInitializing) {
      return;
    }

    try {
      setIsInitializing(true);
      setIsLoading(true);
      setError(null);
      const response = await createConversation();
      const id = response.data.id;
      setConversationId(id);
      localStorage.setItem(CONVERSATION_ID_KEY, id);
      setMessages([]);
    } catch (err) {
      logger.error("Failed to create conversation:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create conversation";
      setError(errorMessage);
      // If it's a rate limit error, don't clear the error state automatically
      if (errorMessage.includes("Too many requests")) {
        // Rate limited - user will see the error message
      }
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  }, [conversationId, isInitializing]);

  const sendChatMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isSending) {
        return;
      }

      let currentConversationId = conversationId;

      // Create conversation if it doesn't exist
      if (!currentConversationId && !isInitializing) {
        try {
          setIsInitializing(true);
          const response = await createConversation();
          currentConversationId = response.data.id;
          setConversationId(currentConversationId);
          localStorage.setItem(CONVERSATION_ID_KEY, currentConversationId);
        } catch (err) {
          logger.error("Failed to create conversation:", err);
          const errorMessage =
            err instanceof Error ? err.message : "Failed to create conversation";
          setError(errorMessage);
          return;
        } finally {
          setIsInitializing(false);
        }
      } else if (isInitializing) {
        // Wait for initialization to complete
        return;
      }

      // Ensure we have a conversation ID before proceeding
      if (!currentConversationId) {
        setError("Failed to get conversation ID. Please try again.");
        return;
      }

      // Add user message optimistically
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        conversationId: currentConversationId,
        role: "user",
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsSending(true);
      setError(null);

      try {
        const response = await sendMessage(currentConversationId, content);
        // Replace with actual messages from server
        setMessages(response.data.messages);
      } catch (err) {
        logger.error("Failed to send message:", err);
        setError(err instanceof Error ? err.message : "Failed to send message");
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
      } finally {
        setIsSending(false);
      }
    },
    [conversationId, isSending, isInitializing]
  );

  return {
    conversationId,
    messages,
    isLoading,
    isSending,
    error,
    sendMessage: sendChatMessage,
    initializeConversation,
    clearError: () => setError(null),
  };
}
